/**
 * Diary sharing service.
 *
 * Wired to the BE `/diary-sharing` endpoints. Schema lives on the
 * Hairdresser model (diaryVisibility + diaryVisibleTo). Salon owners
 * always see all diaries (override) — the BE's /me endpoint encodes
 * that.
 *
 * Public function shapes preserved from the previous stub so calling
 * components don't need to change:
 *   fetchSettings()              → { bulkShare, staff: [...] }
 *   toggleBulkShare(shareAll)    → { success }
 *   updateStaffVisibility(id, p) → { success }
 *   fetchMyVisibility()          → { canSee }
 *
 * Notes:
 * - `bulkShare` isn't a server concept yet — we derive it client-side
 *   from whether every staff member is set to 'everyone'. Toggling
 *   bulkShare patches each row individually.
 * - `sharesWithCurrentUser` is computed locally from the row data and
 *   the current user id, mirroring what the previous stub did.
 */

import axios from "axios";
import API from "../config/API";

const headers = () => {
  const accessToken = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    authorization: "Basic YmV1YXBwOmJldWFwcA==",
    accessToken: `${accessToken}`,
  };
};

const computeSharesWithUser = (currentUserId, targetStaff) => {
  if (!targetStaff) return false;
  if (targetStaff.diaryVisibility === "everyone") return true;
  if (targetStaff.diaryVisibility === "nobody") return false;
  return (targetStaff.diaryVisibleTo || []).includes(currentUserId);
};

// The settings endpoint currently names everyone "Stylist" and sends no
// avatar, so take names and photos from the team list (best effort: if it
// can't be read, the server's rows are used as they are).
async function teamNames() {
  try {
    const res = await axios.post(API.STAFF_LIST_V2, {}, { headers: headers() });
    const list = res?.data?.responseData?.result;
    const byId = {};
    (Array.isArray(list) ? list : []).forEach((u) => {
      if (!u || !u.name) return;
      const entry = { name: u.name, avatarUrl: u.userImage?.circleImage || null };
      if (u._id) byId[String(u._id)] = entry;
      if (u.hairdresserInfo?._id) byId[String(u.hairdresserInfo._id)] = entry;
    });
    return byId;
  } catch (e) {
    return {};
  }
}

async function fetchSettings() {
  const [res, names] = await Promise.all([
    axios.get(API.DIARY_SHARING_SETTINGS, { headers: headers() }),
    teamNames(),
  ]);
  const payload =
    res?.data?.responseData?.result ||
    res?.data?.responseData ||
    res?.data?.data ||
    {};
  const staff = (Array.isArray(payload.staff) ? payload.staff : []).map((s) => {
    const known = names[String(s.hairdresserId)] || names[String(s.userId)];
    return known ? { ...s, name: known.name, avatarUrl: s.avatarUrl || known.avatarUrl } : s;
  });
  // diaryVisibleTo holds hairdresser ids, but localStorage only has the
  // User id — resolve my hairdresser row through its userId.
  const myUserId = localStorage.getItem("userId") || "";
  const meRow = staff.find((s) => s.userId && s.userId === myUserId);
  const currentUserId = meRow ? meRow.hairdresserId : myUserId;

  // Derive bulkShare from row state — true when every staff is on 'everyone'.
  // First-pass approximation; the BE may add a real bulkShare column later.
  // salon.diaryShare is what the diary actually enforces, so it wins when
  // the BE sends it. Older BEs don't, so fall back to the row derivation.
  const bulkShare =
    payload.diaryShare !== undefined
      ? payload.diaryShare === 1
      : staff.length === 0 ||
        staff.every((s) => (s.diaryVisibility || "everyone") === "everyone");

  return {
    bulkShare,
    staff: staff.map((s) => ({
      ...s,
      // BE returns `hairdresserId` as canonical id (matches stub).
      sharesWithCurrentUser: computeSharesWithUser(currentUserId, s),
    })),
  };
}

async function updateStaffVisibility(staffId, { diaryVisibility, diaryVisibleTo }) {
  const url = API.DIARY_SHARING_STAFF + staffId;
  const body = {
    diaryVisibility,
    diaryVisibleTo: diaryVisibility === "selected" ? diaryVisibleTo || [] : [],
  };
  await axios.patch(url, body, { headers: headers() });
  return { success: true };
}

/**
 * No real bulkShare endpoint yet — implement as iterating PATCHs over
 * the salon's staff. Self-employed staff are skipped (they manage their
 * own visibility), matching the prior stub.
 */
async function toggleBulkShare(shareAll) {
  // Owner-only salon switch the diary endpoint checks. Without it staff
  // only ever see their own diary, whatever the rows below say.
  await axios.post(API.DIARY_SHARE, { diaryShare: shareAll ? 1 : 0 }, { headers: headers() });
  const settings = await fetchSettings();
  const updates = settings.staff
    .filter((s) => !(s.employmentTypeId === 2 && !shareAll))
    .map((s) =>
      updateStaffVisibility(s.hairdresserId, {
        diaryVisibility: shareAll
          ? "everyone"
          : s.diaryVisibility === "everyone"
            ? "nobody"
            : s.diaryVisibility,
        diaryVisibleTo: [],
      })
    );
  await Promise.all(updates);
  return { success: true };
}

async function fetchMyVisibility() {
  const res = await axios.get(API.DIARY_SHARING_ME, { headers: headers() });
  const payload =
    res?.data?.responseData?.result ||
    res?.data?.responseData ||
    res?.data?.data ||
    {};

  // Stub contract is `{ canSee }`. BE returns `{ canISee, whoCanSeeMe,
  // iAmOwner }` — map canISee → canSee for backward compat.
  return {
    canSee: payload.canISee || [],
    whoCanSeeMe: payload.whoCanSeeMe || [],
    iAmOwner: !!payload.iAmOwner,
  };
}

export const diarySharingService = {
  fetchSettings,
  toggleBulkShare,
  updateStaffVisibility,
  fetchMyVisibility,
};
